import { useEffect, useState, useReducer } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";

import {
  CardComponent,
  InputEdit,
  SelectEdit,
  ConfirmationModal,
  LoadingSpinner,
} from "components";
import { useGnssApi, useAuth, useToast } from "utils/hooks";
import {
  validateCoord,
  validatePort,
  validateHostname,
  isValidNumber,
} from "utils/helpers/validators";
import { WarningCard } from "../../components";

import { stationDetailsReducer } from "../../utils/reducers/reducers";

import { buildStationDetailsPayload } from "../../utils/helpers/formatters";

export const GNSSEditStations = () => {
  const api = useGnssApi();
  const { openToast } = useToast();
  const { tenant_access_token, getAuthorizationByRoles, verifyAuthorization } =
    useAuth();

  const availableGroups = getAuthorizationByRoles(["Administrator"]);

  const isTenantOwner = verifyAuthorization(["Tenant Owner"]);

  const createInitialState = (availableGroups) => ({
    name: "",
    identifier: "",
    type: "PERFORMANCE",
    isSelfHosted: false,
    groupNames:
      availableGroups.length > 0 ? [availableGroups[0].groupName] : [],

    sis: { hostname: "", port: null },

    int: {
      north: { hostname: "", port: null },
      east: { hostname: "", port: null },
      south: { hostname: "", port: null },
      west: { hostname: "", port: null },
    },

    location: { lat: 0, lon: 0, elv: 0 },
  });

  const [stationsDetails, setStationsDetails] = useState();
  const [newDetails, dispatch] = useReducer(
    stationDetailsReducer,
    availableGroups,
    createInitialState,
  );

  const [modalProps, setModalProps] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "",
  });

  const [selectedStation, setSelectedStation] = useState("");

  const [errorMessages, setErrorMessage] = useState({
    name: null,
    identifier: null,
    sisHostname: null,
    sisPort: null,
    intNorthHostname: null,
    intNorthPort: null,
    intEastHostname: null,
    intEastPort: null,
    intSouthHostname: null,
    intSouthPort: null,
    intWestHostname: null,
    intWestPort: null,
    longitude: null,
    latitude: null,
    elevation: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [warning, setWarning] = useState({ title: "", message: "" });

  const openModal = ({ title, message, onConfirm, type = "primary" }) => {
    setModalProps({
      show: true,
      title,
      message,
      onConfirm,
      type,
    });
  };

  const closeModal = () => {
    setModalProps((prev) => ({ ...prev, show: false }));
  };

  const confirmDelete = () => {
    openModal({
      title: "Delete station",
      message: "Are you sure you want to delete this station?",
      type: "danger",
      onConfirm: () => {
        deleteStation();
      },
    });
  };

  const confirmEdit = () => {
    if (
      !newDetails ||
      !newDetails.name ||
      !newDetails.type ||
      !newDetails.identifier ||
      !isValidNumber(newDetails.location.lat) ||
      !isValidNumber(newDetails.location.lon) ||
      !isValidNumber(newDetails.location.elv)
    ) {
      openToast({
        message: `All mandatory fields are required`,
        type: "danger",
        autohide: true,
        delay: 5000,
      });
      return;
    }

    if (!newDetails.groupNames || newDetails.groupName === "UNGROUPED") {
      openToast({
        message: `Please select a network`,
        type: "danger",
        autohide: true,
        delay: 5000,
      });

      return;
    }

    openModal({
      title: "Edit station",
      message: "Do you want to edit this station?",
      type: "warning",
      onConfirm: () => {
        updateStationDetails(newDetails);
      },
    });
  };

  useEffect(() => {
    getAllStationsDetails();
  }, []);

  const getAllStationsDetails = async () => {
    try {
      setIsLoading(true);
      let response = await api.getAllStationsDetails();

      response = response.data;
      setIsLoading(false);

      if (response.length === 0) {
        setIsSuccess(false);
        setWarning({
          title: "No stations available",
          message: `Your administrator hasn’t granted you access to the stations. Please contact your administrator to request the required permissions.  
  If you are the administrator, please ensure that your account has the necessary permissions and that at least one network and receiver are configured in the platform settings.`,
        });
        return;
      }

      if (response?.length > 0 && !isTenantOwner) {
        response = response?.filter((station) =>
          availableGroups
            .map((group) => group.groupName)
            .some((availableGroupsName) =>
              station.groupNames.includes(availableGroupsName),
            ),
        );
      }

      setStationsDetails(response ?? []);
      response?.length > 0 ? setSelectedStation(response[0]) : {};
      response?.length > 0
        ? dispatch({
            type: "SET_ALL",
            payload: response[0],
          })
        : {};
      setIsSuccess(true);
      return response;
    } catch (e) {
      setIsLoading(false);
      //console.error("Failed to fetch stations", e);

      setIsSuccess(false);
      setWarning({
        title: "No stations available",
        message: `Your administrator hasn't granted you access to the stations. If you are the administrator please check that you have networks and stations added in platform  `,
      });
    }
  };

  const refreshAllStationsDetails = async () => {
    try {
      const response = await api.getAllStationsDetails();
      setStationsDetails(response.data);
      setSelectedStation(newDetails);
      return response;
    } catch (e) {
      //alert(e);
      //throw e;
    }
  };

  const updateStationDetails = async (stationDetailsPayload) => {
    const finalPayload = buildStationDetailsPayload(stationDetailsPayload);

    try {
      const response = await api.updateStationDetails(
        selectedStation.groupNames[0],
        selectedStation.identifier,
        finalPayload,
      );

      refreshAllStationsDetails();
      openToast({
        message: `Station ${selectedStation.name} (${selectedStation.identifier}) updated successfully`,
        type: "success",
        autohide: true,
        delay: 5000,
      });
      return response;
    } catch (e) {
      openToast({
        message: `Station ${selectedStation.name} (${selectedStation.identifier}) cannot be updated`,
        type: "danger",
        autohide: true,
        delay: 5000,
      });
    }
  };

  const deleteStation = async () => {
    try {
      const response = await api.deleteStation(
        selectedStation.groupNames[0],
        selectedStation.identifier,
      );

      if (response.status === "SUCCESS")
        openToast({
          message: `Station ${selectedStation.name} (${selectedStation.identifier}) deleted successfully`,
          type: "success",
          autohide: true,
          delay: 5000,
        });
      getAllStationsDetails();
      return response;
    } catch (e) {
      openToast({
        message: `Station ${selectedStation.name} (${selectedStation.identifier}) cannot be deleted`,
        type: "danger",
        autohide: true,
        delay: 5000,
      });
    }
  };

  if (!availableGroups || availableGroups.length === 0)
    return (
      <WarningCard
        title={"No networks available"}
        message={
          <span>
            Your company doesn't have any networks. In order to add a station
            you need to create a new network. To add a new one{" "}
            <a href="/settings/networks" className="text-decoration-underline ">
              click here
            </a>
          </span>
        }
      />
    );

  if (isLoading) return <LoadingSpinner />;

  if (!isSuccess && !isLoading) return <WarningCard {...warning} />;

  return (
    <>
      <Row className="m-3">
        <CardComponent title={"Edit GNSS Station"}>
          <Row>
            {selectedStation.groupNames.length === 1 &&
              selectedStation.groupNames.includes("UNGROUPED") && (
                <span className="m-0 text-danger">
                  <b>WARNING:</b> This station is not assigned to a network
                </span>
              )}
            <Col
              xs={{ span: 12, offset: 0 }}
              sm={{ span: 12, offset: 0 }}
              md={{ span: 12, offset: 0 }}
              lg={{ span: 12, offset: 0 }}
              xl={{ span: 12, offset: 0 }}
              xxl={{ span: 6, offset: 0 }}
            >
              {stationsDetails ? (
                <>
                  <SelectEdit
                    controlId={"test"}
                    label={"Select station"}
                    onChange={(e) => {
                      dispatch({
                        type: "SET_ALL",
                        payload: stationsDetails?.find((station) => {
                          return station.identifier === e.target.value;
                        }),
                      });

                      setSelectedStation(
                        stationsDetails?.find((station) => {
                          return station.identifier === e.target.value;
                        }),
                      );
                      setErrorMessage({
                        name: null,
                        identifier: null,
                        sisHostname: null,
                        sisPort: null,
                        intHostname: null,
                        intPort: null,
                        longitude: null,
                        latitude: null,
                        elevation: null,
                      });
                    }}
                  >
                    {stationsDetails?.map((station, index) => {
                      return (
                        <option
                          key={index}
                          id={station.id}
                          value={station.identifier}
                        >
                          {selectedStation.groupNames.length === 1 &&
                          selectedStation.groupNames.includes("UNGROUPED")
                            ? station.name + " (no network assigned)"
                            : station.name}
                        </option>
                      );
                    })}
                  </SelectEdit>

                  {selectedStation ? (
                    <Form onSubmit={(e) => e.preventDefault()}>
                      <Form.Group
                        as={Col}
                        controlId="groupName"
                        className="mt-3"
                        style={{
                          flexDirection: "row",
                          alignContent: "flex-start",
                          display: "flex",
                          justifyContent: "flex-start",
                          gap: "1rem",
                        }}
                      >
                        <Form.Label>Networks*:</Form.Label>
                        {tenant_access_token &&
                          availableGroups.map((group, index) => {
                            return (
                              <Form.Check
                                key={`${group.groupName + "-" + index}`}
                                id={`${group.groupName + "-" + index}`}
                                value={group.groupName}
                                aria-label="radio 2"
                                label={group.groupName}
                                onChange={(e) => {
                                  dispatch({
                                    type: "TOGGLE_GROUP",
                                    group: group.groupName,
                                  });
                                }}
                                checked={newDetails.groupNames?.includes(
                                  group.groupName,
                                )}
                              />
                            );
                          })}
                      </Form.Group>
                      <Form.Group
                        as={Col}
                        controlId="stationType"
                        className="mt-3"
                        style={{
                          flexDirection: "row",
                          alignContent: "flex-start",
                          display: "flex",
                          justifyContent: "flex-start",
                          gap: "1rem",
                        }}
                      >
                        <Form.Label>Choose station type*:</Form.Label>
                        <Form.Check
                          id="stationType-1"
                          value={"PERFORMANCE"}
                          type="radio"
                          aria-label="radio 2"
                          label={"Performance"}
                          onChange={(e) => {
                            setErrorMessage({
                              ...errorMessages,
                              intHostname: null,
                              intPort: null,
                            });
                            dispatch({
                              type: "SET_FIELD",
                              field: "type",
                              value: "PERFORMANCE",
                            });
                          }}
                          checked={newDetails.type === "PERFORMANCE"}
                        />
                        <Form.Check
                          id="stationType-2"
                          value="LOCALIZATION"
                          type="radio"
                          label="Localization"
                          checked={newDetails.type === "LOCALIZATION"}
                          onChange={() =>
                            dispatch({
                              type: "SET_FIELD",
                              field: "type",
                              value: "LOCALIZATION",
                            })
                          }
                        />
                        <Form.Check
                          id="stationType-3"
                          value={"PERFORMANCE_AND_LOCALIZATION"}
                          type="radio"
                          aria-label="radio 3"
                          label={"Performance and localization"}
                          onChange={(e) => {
                            dispatch({
                              type: "SET_FIELD",
                              field: "type",
                              value: "PERFORMANCE_AND_LOCALIZATION",
                            });
                          }}
                          checked={
                            newDetails.type === "PERFORMANCE_AND_LOCALIZATION"
                          }
                        />
                      </Form.Group>
                      <Form.Group
                        as={Col}
                        controlId="selfHosted"
                        className="mt-3"
                        style={{
                          flexDirection: "row",
                          alignContent: "flex-start",
                          display: "flex",
                          justifyContent: "flex-start",
                          gap: "1rem",
                        }}
                      >
                        <Form.Label>Self-hosted* :</Form.Label>
                        <Form.Check
                          id="stationType-1"
                          value="PERFORMANCE"
                          type="switch"
                          checked={newDetails.isSelfHosted}
                          onChange={() => {
                            if (
                              !selectedStation.isSelfHosted &&
                              !newDetails.isSelfHosted
                            ) {
                              dispatch({
                                type: "DISABLE_SIS_INT",
                              });
                            }

                            dispatch({
                              type: "SET_FIELD",
                              field: "isSelfHosted",
                              value: !newDetails.isSelfHosted,
                            });
                          }}
                        />
                      </Form.Group>
                      <Form.Group
                        as={Col}
                        className="mb-3"
                        controlId="stationName"
                      >
                        <InputEdit
                          label={"Change station name*"}
                          placeholder={"The name of GNSS monitoring station"}
                          value={selectedStation.name || ""}
                          error={errorMessages.name}
                          validationPattern={/[^a-zA-Z0-9 -]/g}
                          onFocus={(e) => {
                            setErrorMessage({
                              ...errorMessages,
                              name: null,
                            });
                          }}
                          onBlur={(e) => {
                            if (
                              e.target.value === null ||
                              e.target.value === ""
                            )
                              setErrorMessage({
                                ...errorMessages,
                                name: "Field cannot be empty",
                              });
                          }}
                          onChange={(e) => {
                            dispatch({
                              type: "SET_FIELD",
                              field: "name",
                              value: e.target.value,
                            });
                          }}
                        />
                      </Form.Group>
                      <Form.Group as={Col} className="mb-3">
                        <InputEdit
                          label={"Station identifier*"}
                          disabled={true}
                          placeholder={"4-character ID"}
                          value={selectedStation.identifier || ""}
                          error={errorMessages.identifier}
                          validationPattern={/[^a-zA-Z0-9]/g}
                          onFocus={(e) => {
                            setErrorMessage({
                              ...errorMessages,
                              identifier: null,
                            });
                          }}
                          onBlur={(e) =>
                            e.target.value.trim() === "" &&
                            setErrorMessage({
                              ...errorMessages,
                              identifier: "Field cannot be empty",
                            })
                          }
                          onChange={(e) =>
                            dispatch({
                              type: "SET_FIELD",
                              field: "identifier",
                              value: e.target.value.trim(),
                            })
                          }
                        />
                      </Form.Group>

                      <Form.Group as={Row} controlId="locationFields">
                        {["latitude", "longitude", "elevation"].map((coord) => {
                          const key =
                            coord === "longitude"
                              ? "lon"
                              : coord === "latitude"
                                ? "lat"
                                : "elv";

                          return (
                            <Form.Group
                              as={Col}
                              controlId={`form-${coord}`}
                              key={coord}
                            >
                              <InputEdit
                                label={`${
                                  coord.charAt(0).toUpperCase() + coord.slice(1)
                                }*`}
                                error={errorMessages[coord]}
                                onFocus={() =>
                                  setErrorMessage({
                                    ...errorMessages,
                                    [coord]: null,
                                  })
                                }
                                placeholder={
                                  coord === "elevation"
                                    ? "Meters"
                                    : "DD (Decimal Degrees)"
                                }
                                value={selectedStation.location[key] || "0"}
                                onBlur={(e) =>
                                  setErrorMessage({
                                    ...errorMessages,
                                    [coord]: validateCoord(
                                      e.target.value,
                                      coord,
                                    ),
                                  })
                                }
                                onChange={(e) =>
                                  dispatch({
                                    type: "SET_LOCATION",
                                    field:
                                      coord === "longitude"
                                        ? "lon"
                                        : coord === "latitude"
                                          ? "lat"
                                          : "elv",
                                    value: e.target.value.trim(),
                                  })
                                }
                              />
                            </Form.Group>
                          );
                        })}
                      </Form.Group>

                      {["PERFORMANCE", "PERFORMANCE_AND_LOCALIZATION"].includes(
                        newDetails.type,
                      ) &&
                        newDetails.isSelfHosted && (
                          <Form.Group as={Row} controlId="sisConnection">
                            <Form.Group as={Col} controlId="sisHostname">
                              <InputEdit
                                label={"Performance receiver hostname"}
                                placeholder={"Hostname/IP address"}
                                value={newDetails?.sis?.hostname || ""}
                                error={errorMessages.sisHostname}
                                cleanWhitespaces={true}
                                onFocus={() =>
                                  setErrorMessage({
                                    ...errorMessages,
                                    sisHostname: null,
                                  })
                                }
                                onBlur={(e) => {
                                  if (
                                    !validateHostname(e.target.value) &&
                                    e.target.value
                                  )
                                    setErrorMessage({
                                      ...errorMessages,
                                      sisHostname: "Invalid format",
                                    });
                                }}
                                onChange={(e) => {
                                  dispatch({
                                    type: "SET_SIS",
                                    field: "hostname",
                                    value: e.target.value,
                                  });
                                }}
                              />
                            </Form.Group>
                            <Form.Group as={Col} md={4} controlId="sisPort">
                              <InputEdit
                                label={"Port"}
                                type={"number"}
                                placeholder={"Default 80"}
                                value={newDetails?.sis?.port || ""}
                                error={errorMessages.sisPort}
                                onFocus={(e) => {
                                  setErrorMessage({
                                    ...errorMessages,
                                    sisPort: null,
                                  });
                                }}
                                onBlur={(e) => {
                                  !validatePort(e.target.value) &&
                                    setErrorMessage({
                                      ...errorMessages,
                                      sisPort: "Invalid or out of range",
                                    });
                                }}
                                onChange={(e) => {
                                  dispatch({
                                    type: "SET_SIS",
                                    field: "port",
                                    value: e.target.value,
                                  });
                                }}
                              />
                            </Form.Group>
                          </Form.Group>
                        )}

                      {[
                        "LOCALIZATION",
                        "PERFORMANCE_AND_LOCALIZATION",
                      ].includes(newDetails.type) &&
                        newDetails.isSelfHosted && (
                          <>
                            <Form.Group as={Row} controlId="intNorthConnection">
                              <Form.Group as={Col} controlId="intNorthHostname">
                                <InputEdit
                                  label={
                                    "Localization receiver hostname (North)"
                                  }
                                  placeholder={
                                    "Hostname/IP address of North oriented receiver"
                                  }
                                  value={newDetails?.int?.north?.hostname || ""}
                                  error={errorMessages.intHostname}
                                  cleanWhitespaces={true}
                                  onFocus={() =>
                                    setErrorMessage({
                                      ...errorMessages,
                                      intNorthHostname: null,
                                    })
                                  }
                                  onBlur={(e) => {
                                    if (
                                      !validateHostname(e.target.value) &&
                                      e.target.value
                                    )
                                      setErrorMessage({
                                        ...errorMessages,
                                        intNorthHostname: "Invalid format",
                                      });
                                  }}
                                  onChange={(e) => {
                                    dispatch({
                                      type: "SET_INT",
                                      direction: "north",
                                      field: "hostname",
                                      value: e.target.value,
                                    });
                                  }}
                                />
                              </Form.Group>

                              <Form.Group
                                as={Col}
                                md={4}
                                controlId="intNorthPort"
                              >
                                <InputEdit
                                  label={"Port (North)"}
                                  type={"number"}
                                  placeholder={"Default 80"}
                                  value={newDetails?.int?.north?.port || ""}
                                  error={errorMessages.intPort}
                                  onFocus={(e) => {
                                    setErrorMessage({
                                      ...errorMessages,
                                      intNorthPort: null,
                                    });
                                  }}
                                  onBlur={(e) => {
                                    !validatePort(e.target.value) &&
                                      setErrorMessage({
                                        ...errorMessages,
                                        intNorthPort: "Invalid or out of range",
                                      });
                                  }}
                                  onChange={(e) => {
                                    dispatch({
                                      type: "SET_INT",
                                      direction: "north",
                                      field: "port",
                                      value: e.target.value,
                                    });
                                  }}
                                />
                              </Form.Group>
                            </Form.Group>
                            <Form.Group as={Row} controlId="intEastConnection">
                              <Form.Group as={Col} controlId="intEastHostname">
                                <InputEdit
                                  label={
                                    "Localization receiver hostname (East)"
                                  }
                                  placeholder={
                                    "Hostname/IP address of East oriented receiver"
                                  }
                                  value={newDetails?.int?.east?.hostname || ""}
                                  error={errorMessages.intHostname}
                                  cleanWhitespaces={true}
                                  onFocus={() =>
                                    setErrorMessage({
                                      ...errorMessages,
                                      intEastHostname: null,
                                    })
                                  }
                                  onBlur={(e) => {
                                    if (
                                      !validateHostname(e.target.value) &&
                                      e.target.value
                                    )
                                      setErrorMessage({
                                        ...errorMessages,
                                        intEastHostname: "Invalid format",
                                      });
                                  }}
                                  onChange={(e) => {
                                    dispatch({
                                      type: "SET_INT",
                                      direction: "east",
                                      field: "hostname",
                                      value: e.target.value,
                                    });
                                  }}
                                />
                              </Form.Group>

                              <Form.Group
                                as={Col}
                                md={4}
                                controlId="intEastPort"
                              >
                                <InputEdit
                                  label={"Port (East)"}
                                  type={"number"}
                                  placeholder={"Default 80"}
                                  value={newDetails?.int?.east?.port || ""}
                                  error={errorMessages.intEastPort}
                                  onFocus={(e) => {
                                    setErrorMessage({
                                      ...errorMessages,
                                      intPort: null,
                                    });
                                  }}
                                  onBlur={(e) => {
                                    !validatePort(e.target.value) &&
                                      setErrorMessage({
                                        ...errorMessages,
                                        intEastPort: "Invalid or out of range",
                                      });
                                  }}
                                  onChange={(e) => {
                                    dispatch({
                                      type: "SET_INT",
                                      direction: "east",
                                      field: "port",
                                      value: e.target.value,
                                    });
                                  }}
                                />
                              </Form.Group>
                            </Form.Group>
                            <Form.Group as={Row} controlId="intSouthConnection">
                              <Form.Group as={Col} controlId="intSouthHostname">
                                <InputEdit
                                  label={
                                    "Localization receiver hostname (South)"
                                  }
                                  placeholder={
                                    "Hostname/IP address of South oriented receiver"
                                  }
                                  value={newDetails?.int?.south?.hostname || ""}
                                  error={errorMessages.intHostname}
                                  cleanWhitespaces={true}
                                  onFocus={() =>
                                    setErrorMessage({
                                      ...errorMessages,
                                      intHostname: null,
                                    })
                                  }
                                  onBlur={(e) => {
                                    if (
                                      !validateHostname(e.target.value) &&
                                      e.target.value
                                    )
                                      setErrorMessage({
                                        ...errorMessages,
                                        intHostname: "Invalid format",
                                      });
                                  }}
                                  onChange={(e) => {
                                    dispatch({
                                      type: "SET_INT",
                                      direction: "south",
                                      field: "hostname",
                                      value: e.target.value,
                                    });
                                  }}
                                />
                              </Form.Group>

                              <Form.Group
                                as={Col}
                                md={4}
                                controlId="intSouthPort"
                              >
                                <InputEdit
                                  label={"Port (South)"}
                                  type={"number"}
                                  placeholder={"Default 80"}
                                  value={newDetails?.int?.south?.port || ""}
                                  error={errorMessages.intPort}
                                  onFocus={(e) => {
                                    setErrorMessage({
                                      ...errorMessages,
                                      intPort: null,
                                    });
                                  }}
                                  onBlur={(e) => {
                                    !validatePort(e.target.value) &&
                                      setErrorMessage({
                                        ...errorMessages,
                                        intPort: "Invalid or out of range",
                                      });
                                  }}
                                  onChange={(e) => {
                                    dispatch({
                                      type: "SET_INT",
                                      direction: "south",
                                      field: "port",
                                      value: e.target.value,
                                    });
                                  }}
                                />
                              </Form.Group>
                            </Form.Group>
                            <Form.Group as={Row} controlId="intWestConnection">
                              <Form.Group as={Col} controlId="intWestHostname">
                                <InputEdit
                                  label={
                                    "Localization receiver hostname (West)"
                                  }
                                  placeholder={
                                    "Hostname/IP address of West oriented receiver"
                                  }
                                  value={newDetails?.int?.west?.hostname || ""}
                                  error={errorMessages.intHostname}
                                  cleanWhitespaces={true}
                                  onFocus={() =>
                                    setErrorMessage({
                                      ...errorMessages,
                                      intHostname: null,
                                    })
                                  }
                                  onBlur={(e) => {
                                    if (
                                      !validateHostname(e.target.value) &&
                                      e.target.value
                                    )
                                      setErrorMessage({
                                        ...errorMessages,
                                        intHostname: "Invalid format",
                                      });
                                  }}
                                  onChange={(e) => {
                                    dispatch({
                                      type: "SET_INT",
                                      direction: "west",
                                      field: "hostname",
                                      value: e.target.value,
                                    });
                                  }}
                                />
                              </Form.Group>

                              <Form.Group
                                as={Col}
                                md={4}
                                controlId="intWestPort"
                              >
                                <InputEdit
                                  label={"Port (West)"}
                                  type={"number"}
                                  placeholder={"Default 80"}
                                  value={newDetails?.int?.west?.port || ""}
                                  error={errorMessages.intPort}
                                  onFocus={(e) => {
                                    setErrorMessage({
                                      ...errorMessages,
                                      intPort: null,
                                    });
                                  }}
                                  onBlur={(e) => {
                                    !validatePort(e.target.value) &&
                                      setErrorMessage({
                                        ...errorMessages,
                                        intPort: "Invalid or out of range",
                                      });
                                  }}
                                  onChange={(e) => {
                                    dispatch({
                                      type: "SET_INT",
                                      direction: "west",
                                      field: "port",
                                      value: e.target.value,
                                    });
                                  }}
                                />
                              </Form.Group>
                            </Form.Group>
                          </>
                        )}
                      <Form.Group as={Row} className="mb-3">
                        <Col>
                          <p className="text-warning">
                            All (*) fields are mandatory
                          </p>
                        </Col>
                        <Col className="text-end">
                          <Button
                            className="m-1"
                            variant="danger"
                            onClick={() => confirmDelete()}
                          >
                            Remove station
                          </Button>
                          <Button
                            className="m-1"
                            variant="success"
                            disabled={Object.values(errorMessages).some(
                              (value) => value !== null,
                            )}
                            onClick={() => confirmEdit()}
                          >
                            Apply changes
                          </Button>
                        </Col>
                      </Form.Group>
                    </Form>
                  ) : (
                    <></>
                  )}
                </>
              ) : (
                <></>
              )}
            </Col>
          </Row>
        </CardComponent>
      </Row>
      <ConfirmationModal {...modalProps} onClose={closeModal} />
    </>
  );
};
